import { useEffect, useState } from "react";
import { API_CACHE_TTL, API_MISS_TTL, fetchOptionalGetApi, isAbortError } from "@/utils/apiCache";

interface SpotifyTrack {
  name: string;
  artists?: { name: string }[];
}

interface UseSearchYouTubeProps {
  artistName: string;
  songName?: string;
  spotifyTracks?: SpotifyTrack[];
}

interface UseSearchYouTubeOptions {
  maxResults?: number;
}

interface YouTubeVideo {
  id: {
    videoId: string;
  };
  snippet: {
    title: string;
    description: string;
    thumbnails: {
      default: { url: string };
      medium: { url: string };
      high: { url: string };
    };
    channelTitle: string;
    publishedAt: string;
  };
}

interface YouTubeSearchResponse {
  items: YouTubeVideo[];
  pageInfo: {
    totalResults: number;
    resultsPerPage: number;
  };
}

interface UseSearchYouTubeReturn {
  data: YouTubeVideo[] | null;
  isLoading: boolean;
  error: string | null;
}

function useSearchYouTube(
  props: UseSearchYouTubeProps,
  options: UseSearchYouTubeOptions = { maxResults: 10 },
  dependencies: React.DependencyList = []
): UseSearchYouTubeReturn {
  const [youtubeData, setYoutubeData] = useState<YouTubeVideo[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { artistName, songName, spotifyTracks } = props;
  const { maxResults = 10 } = options;

  async function fetchYouTubeData(
    artist: string,
    track: string,
    maxResults: number,
    tracks: SpotifyTrack[] | undefined,
    signal: AbortSignal
  ): Promise<YouTubeVideo[] | null> {
    if (typeof window === "undefined") {
      return null;
    }

    const data = await fetchOptionalGetApi<YouTubeSearchResponse>(`/.netlify/functions/GetYoutubeVideo`, {
      artist,
      trackName: track || "",
      maxResults,
      spotifyTracks: (tracks || []).map((item) => item.name).filter(Boolean),
    }, {
      ttlMs: API_CACHE_TTL.SIX_HOURS,
      missTtlMs: API_MISS_TTL.ONE_DAY,
      shouldCache: (data) => Boolean(data?.items?.length),
      isMiss: (data) => !data?.items?.length,
      signal,
    });
    console.log("YouTube search data:", data);

    return data?.items?.length ? data.items : null;
  }

  useEffect(() => {
    if (typeof window === "undefined" || !artistName) return;

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    fetchYouTubeData(artistName, songName || "", maxResults, spotifyTracks, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setYoutubeData(data);
        setError(data ? null : "No videos found");
        setIsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        console.warn("YouTube search unavailable:", err);
        setError("No videos found");
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [...dependencies, maxResults, JSON.stringify(spotifyTracks)]);

  return { data: youtubeData, isLoading, error };
}

export default useSearchYouTube;
